import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { QueueNowData, QueueTodayData } from '../../models/reports.external';
import { defaultQueueReport, IQueueReport, ReportDictionary } from '../../models/reports.internal';
import { objectValues } from '../../utils/objectUtils';
import { SignalrDataContext } from '../../providers/SignalrDataProvider';
import { TemporaryDataCacheContext } from '../..//providers/TemporaryDataCacheProvider';
import useLazyUpdateState from '../../shared/hooks/useLazyUpdateState';
import { processQueueNow, processQueueToday } from '../../calculations/queueCalculations';
import { ReportDataState } from '../../components/shared/ReportList';

const cacheKey = 'useGetQueueReports-queuesDict';

function get(dictionary: ReportDictionary<IQueueReport>, id: string): IQueueReport {
  return dictionary[id] ?? defaultQueueReport(id);
}

export default function useGetQueueReports(): ReportDataState<IQueueReport> {
  const { retrieveData, storeData } = useContext(TemporaryDataCacheContext);
  const { subscribe, unsubscribe, myTeams } = useContext(SignalrDataContext);

  const [queuesDict, setQueuesDict] = useLazyUpdateState<ReportDictionary<IQueueReport>>({});
  const queuesDictRef = useRef(queuesDict);
  queuesDictRef.current = queuesDict;

  //#region [1] Retrieve from and store to cache
  useEffect(
    () => {
      const cachedQueues = retrieveData<ReportDictionary<IQueueReport>>(cacheKey);
      
      if (cachedQueues !== undefined)
        setQueuesDict({ ...cachedQueues });

      return () => storeData(cacheKey, queuesDictRef.current, 10);
    },
    [retrieveData, setQueuesDict, storeData],
  );
  //#endregion

  //#region [2] Aggregate updated data into queuesDict
  const receiveTodaysQueueData = useCallback(
    (data: QueueTodayData) => setQueuesDict(dict => {
      const queue = get(dict, data.id);
      const processed = processQueueToday(data);
      const merged: IQueueReport = { ...queue, ...processed };

      return { ...dict, [merged.id]: merged };
    }),
    [setQueuesDict],
  );

  const receiveNowQueueData = useCallback(
    (data: QueueNowData) => setQueuesDict(dict => {
      const queue = get(dict, data.id);
      const processed = processQueueNow(data);
      const merged: IQueueReport = { ...queue, ...processed };

      return { ...dict, [merged.id]: merged };
    }),
    [setQueuesDict],
  );
  //#endregion

  //#region [3] Manage subscription
  useEffect(
    () => {
      const subId = subscribe({ receiveTodaysQueueData, receiveNowQueueData });
      return () => unsubscribe(subId);
    },
    [receiveNowQueueData, receiveTodaysQueueData, subscribe, unsubscribe],
  );
  //#endregion

  const queueReports = useMemo<IQueueReport[]>(
    () => objectValues(queuesDict),
    [queuesDict],
  );

  const result = useMemo<ReportDataState<IQueueReport>>(
    () => ({
      items: queueReports,
      isLoading: queueReports.length === 0,
      willNotLoad: myTeams.length === 0,
    }),
    [myTeams.length, queueReports],
  );

  return result;
}